import React from 'react';
import { ErrorMessage } from 'formik';
import { Radio, Checkbox, Space, Typography } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import {
  StepTitle,
  InfoBox,
  FormSection,
  FormItem,
  SectionTitle,
  ErrorText,
  LargeCheckbox,
  LargeRadio,
  inlineStyles
} from '../styled';

const { Paragraph } = Typography;

const medicalConditionOptions = [
  { value: 'cancer', label: 'Cancer' },
  { value: 'heartProblems', label: 'Heart Problems' },
  { value: 'diabetes', label: 'Diabetes/Sugar' },
  { value: 'mentalHealthDisorders', label: 'Mental Health Disorders (e.g., bipolar, depression, anxiety, etc.)' },
  { value: 'obesity', label: 'Obesity' },
  { value: 'highBloodPressure', label: 'High Blood Pressure' },
  { value: 'badCholesterol', label: 'Bad Cholesterol' },
  { value: 'liverDisease', label: 'Liver Disease' },
  { value: 'kidneyDisease', label: 'Kidney Disease' },
  { value: 'stress', label: 'Stress' },
  { value: 'none', label: 'None of the above' }
];

const StepMedicalHistory = ({ values, setFieldValue, isFieldVisible = () => true }) => {
  const hasMainMedFields = isFieldVisible('heartAttackOrStroke') || isFieldVisible('stressLevel') || isFieldVisible('mentalHealthInterest') || isFieldVisible('isParentOrCaregiver') || isFieldVisible('currentMedications') || isFieldVisible('physicalActivityFrequency') || isFieldVisible('resourceAccess') || isFieldVisible('careAlternative');
  const hasDiagnosisFields = isFieldVisible('medicalConditions') || isFieldVisible('referredToCardiologist');

  return (
  <>
    <StepTitle level={4}>Your Health Information</StepTitle>
    <InfoBox>
      <div className="icon"><InfoCircleOutlined /></div>
      <div className="content">
        <Paragraph style={inlineStyles.infoParagraph}>
          This confidential information helps us assess your heart health needs. Your privacy is important to us.
        </Paragraph>
      </div>
    </InfoBox>

    {hasMainMedFields && (
    <FormSection>
      {isFieldVisible('heartAttackOrStroke') && (
      <FormItem>
        <SectionTitle>Ever suffered a heart attack or stroke?</SectionTitle>
        <div style={inlineStyles.topMarginDiv}>
          <Checkbox.Group
            style={inlineStyles.spaceVertical}
            value={values.heartAttackOrStroke}
            onChange={(checkedValues) => {
              if (checkedValues.includes('none')) {
                setFieldValue('heartAttackOrStroke', ['none']);
              } else if (values.heartAttackOrStroke.includes('none')) {
                setFieldValue('heartAttackOrStroke', checkedValues.filter(v => v !== 'none'));
              } else {
                setFieldValue('heartAttackOrStroke', checkedValues);
              }
            }}
          >
            <Space direction="vertical" size="large" style={inlineStyles.spaceVertical}>
              <LargeCheckbox value="heartAttack">Heart Attack</LargeCheckbox>
              <LargeCheckbox value="stroke">Stroke</LargeCheckbox>
              <LargeCheckbox value="none">None</LargeCheckbox>
            </Space>
          </Checkbox.Group>
        </div>
      </FormItem>
      )}

      {isFieldVisible('stressLevel') && (
      <FormItem>
        <SectionTitle>Please rate your normal stress level</SectionTitle>
        <div style={inlineStyles.topMarginDiv}>
          <Radio.Group
            value={values.stressLevel}
            onChange={(e) => setFieldValue('stressLevel', e.target.value)}
          >
            <Space direction="horizontal" size="large" wrap>
              <LargeRadio value="1">1</LargeRadio>
              <LargeRadio value="2">2</LargeRadio>
              <LargeRadio value="3">3</LargeRadio>
              <LargeRadio value="4">4</LargeRadio>
              <LargeRadio value="5">5</LargeRadio>
            </Space>
          </Radio.Group>
        </div>
        <ErrorMessage name="stressLevel" component={ErrorText} />
      </FormItem>
      )}

      {isFieldVisible('mentalHealthInterest') && (
      <FormItem>
        <SectionTitle>Would you be interested in our free mental health services?</SectionTitle>
        <div style={inlineStyles.topMarginDiv}>
          <Radio.Group
            value={values.mentalHealthInterest}
            onChange={(e) => setFieldValue('mentalHealthInterest', e.target.value)}
          >
            <Space direction="horizontal" size="large" wrap>
              <LargeRadio value="yes">Yes</LargeRadio>
              <LargeRadio value="no">No</LargeRadio>
            </Space>
          </Radio.Group>
        </div>
        <ErrorMessage name="mentalHealthInterest" component={ErrorText} />
      </FormItem>
      )}

      {isFieldVisible('isParentOrCaregiver') && (
      <FormItem>
        <SectionTitle>Are you a parent or caregiver?</SectionTitle>
        <div style={inlineStyles.topMarginDiv}>
          <Radio.Group
            value={values.isParentOrCaregiver}
            onChange={(e) => setFieldValue('isParentOrCaregiver', e.target.value)}
          >
            <Space direction="horizontal" size="large" wrap>
              <LargeRadio value="yes">Yes</LargeRadio>
              <LargeRadio value="no">No</LargeRadio>
              <LargeRadio value="prefer not to say">Prefer not to say</LargeRadio>
            </Space>
          </Radio.Group>
        </div>
        <ErrorMessage name="isParentOrCaregiver" component={ErrorText} />
      </FormItem>
      )}

      {isFieldVisible('currentMedications') && (
      <FormItem>
        <SectionTitle>Are you currently taking medications for any of the following?</SectionTitle>
        <div style={inlineStyles.topMarginDiv}>
          <Checkbox.Group
            style={inlineStyles.spaceVertical}
            value={values.currentMedications}
            onChange={(checkedValues) => {
              if (checkedValues.includes('none')) {
                setFieldValue('currentMedications', ['none']);
              } else if (checkedValues.includes('prefer not to say')) {
                setFieldValue('currentMedications', ['prefer not to say']);
              } else if (values.currentMedications.includes('none') || values.currentMedications.includes('prefer not to say')) {
                setFieldValue('currentMedications', checkedValues.filter(v => v !== 'none' && v !== 'prefer not to say'));
              } else {
                setFieldValue('currentMedications', checkedValues);
              }
            }}
          >
            <Space direction="vertical" size="large" style={inlineStyles.spaceVertical}>
              <LargeCheckbox value="blood pressure">Blood Pressure</LargeCheckbox>
              <LargeCheckbox value="cholesterol">Cholesterol</LargeCheckbox>
              <LargeCheckbox value="diabetes">Diabetes</LargeCheckbox>
              <LargeCheckbox value="heart disease">Heart Disease</LargeCheckbox>
              <LargeCheckbox value="other">Other</LargeCheckbox>
              <LargeCheckbox value="none">None</LargeCheckbox>
              <LargeCheckbox value="prefer not to say">Prefer not to say</LargeCheckbox>
            </Space>
          </Checkbox.Group>
        </div>
        <ErrorMessage name="currentMedications" component={ErrorText} />
      </FormItem>
      )}

      {isFieldVisible('physicalActivityFrequency') && (
      <FormItem>
        <SectionTitle>How often do you engage in physical activity?</SectionTitle>
        <div style={inlineStyles.topMarginDiv}>
          <Radio.Group
            value={values.physicalActivityFrequency}
            onChange={(e) => setFieldValue('physicalActivityFrequency', e.target.value)}
          >
            <Space direction="vertical" size="large">
              <LargeRadio value="daily">Daily</LargeRadio>
              <LargeRadio value="a few times a week">A few times a week</LargeRadio>
              <LargeRadio value="rarely">Rarely</LargeRadio>
              <LargeRadio value="never">Never</LargeRadio>
            </Space>
          </Radio.Group>
        </div>
        <ErrorMessage name="physicalActivityFrequency" component={ErrorText} />
      </FormItem>
      )}

      {isFieldVisible('resourceAccess') && (
      <FormItem>
        <SectionTitle>Which of the following do you currently have access to? (Check all that apply)</SectionTitle>
        <div style={inlineStyles.topMarginDiv}>
          <Checkbox.Group
            style={inlineStyles.spaceVertical}
            value={values.resourceAccess}
            onChange={(checkedValues) => {
              if (checkedValues.includes('none of the above')) {
                setFieldValue('resourceAccess', ['none of the above']);
              } else if (values.resourceAccess.includes('none of the above')) {
                setFieldValue('resourceAccess', checkedValues.filter(v => v !== 'none of the above'));
              } else {
                setFieldValue('resourceAccess', checkedValues);
              }
            }}
          >
            <Space direction="vertical" size="large" style={inlineStyles.spaceVertical}>
              <LargeCheckbox value="healthy affordable food">Healthy, affordable food</LargeCheckbox>
              <LargeCheckbox value="reliable transportation">Reliable transportation</LargeCheckbox>
              <LargeCheckbox value="health insurance">Health insurance</LargeCheckbox>
              <LargeCheckbox value="internet access at home">Internet access at home</LargeCheckbox>
              <LargeCheckbox value="a primary care doctor">A primary care doctor</LargeCheckbox>
              <LargeCheckbox value="none of the above">None of the above</LargeCheckbox>
            </Space>
          </Checkbox.Group>
        </div>
        <ErrorMessage name="resourceAccess" component={ErrorText} />
      </FormItem>
      )}

      {isFieldVisible('careAlternative') && (
      <FormItem>
        <SectionTitle>If this screening were not available today, where would you have gone for care?</SectionTitle>
        <div style={inlineStyles.topMarginDiv}>
          <Radio.Group
            value={values.careAlternative}
            onChange={(e) => setFieldValue('careAlternative', e.target.value)}
          >
            <Space direction="vertical" size="large">
              <LargeRadio value="emergencyRoom">Emergency room</LargeRadio>
              <LargeRadio value="urgentCare">Urgent care</LargeRadio>
              <LargeRadio value="primaryCare">My primary care doctor</LargeRadio>
              <LargeRadio value="retailClinic">A retail / pharmacy clinic</LargeRadio>
              <LargeRadio value="nowhere">I would not have sought care</LargeRadio>
              <LargeRadio value="other">Other</LargeRadio>
            </Space>
          </Radio.Group>
        </div>
        <ErrorMessage name="careAlternative" component={ErrorText} />
      </FormItem>
      )}
    </FormSection>
    )}

    {hasDiagnosisFields && (
    <FormSection>
      {isFieldVisible('medicalConditions') && (
      <FormItem>
        <SectionTitle>Have you ever been diagnosed with any of the following?</SectionTitle>
        <div style={inlineStyles.topMarginDiv}>
          <Checkbox.Group
            style={inlineStyles.spaceVertical}
            value={values.medicalConditions}
            onChange={(checkedValues) => {
              if (checkedValues.includes('none')) {
                setFieldValue('medicalConditions', ['none']);
              } else if (values.medicalConditions.includes('none')) {
                setFieldValue('medicalConditions', checkedValues.filter(v => v !== 'none'));
              } else {
                setFieldValue('medicalConditions', checkedValues);
              }
            }}
          >
            <Space direction="vertical" size="large" style={inlineStyles.spaceVertical}>
              {medicalConditionOptions.map(option => (
                <LargeCheckbox key={option.value} value={option.value}>
                  {option.label}
                </LargeCheckbox>
              ))}
            </Space>
          </Checkbox.Group>
        </div>
        <ErrorMessage name="medicalConditions" component={ErrorText} />
      </FormItem>
      )}

      {isFieldVisible('referredToCardiologist') && values.medicalConditions && values.medicalConditions.length > 0 && !values.medicalConditions.includes('none') && (
        <FormItem>
          <SectionTitle>Have you ever seen/been referred to a cardiologist/heart doctor since being diagnosed with any of the above conditions?</SectionTitle>
          <div style={inlineStyles.topMarginDiv}>
            <Radio.Group
              value={values.referredToCardiologist}
              onChange={(e) => setFieldValue('referredToCardiologist', e.target.value)}
            >
              <Space direction="horizontal" size="large" wrap>
                <LargeRadio value="yes">Yes</LargeRadio>
                <LargeRadio value="no">No</LargeRadio>
              </Space>
            </Radio.Group>
          </div>
          <ErrorMessage name="referredToCardiologist" component={ErrorText} />
        </FormItem>
      )}
    </FormSection>
    )}
  </>
  );
};

export default StepMedicalHistory;
